/**
 * PersonIdentity — who a record names, resolved once and carried whole.
 *
 * THE POINT IS THAT HALF OF IT CANNOT BE EXPRESSED. Records across the platform name a human by
 * assembling the parts themselves: an account id here, a name beside it, a person reference
 * somewhere else. Nothing stops those drifting, and a record that names one human by its account and
 * another by its person reference is wrong in a way nothing detects.
 *
 * THE BACKEND OWNS THE DEFINITION AND THIS CONFORMS. Identity & Tenancy publishes the language and
 * this is the same value on the other runtime, not a second opinion about it. Anything this file
 * decides for itself is a fork, and a fork is how the two ends of one wire start disagreeing about
 * what they are sending each other.
 *
 * WHAT A GUARANTEE SURVIVES. On the backend a value can only come from resolving an account against
 * storage. Nothing survives serialisation, so here the wire carries the ingredients and this
 * reconstructs from them. That is genuinely weaker — this cannot know the trio was ever resolved
 * together — and it is the most a client can promise. What it still buys is that the three travel as
 * one frozen thing, so no screen can pair one person's id with another person's name.
 *
 * NEUTRAL ABOUT ROLE, DELIBERATELY. A clinical activity was performed by somebody, a form was
 * completed by somebody, a signature was made by somebody, and those are three different words for
 * the same human. The role belongs to the field that holds this value, not to the value.
 *
 * THE PAIR IS NOT SYMMETRIC. `personId` is the durable half and is always present. `userId` is the
 * account that happened to be in play, and is legitimately absent: a person record can exist with no
 * account at all, and an account can be deleted outright while the person stays named on records.
 *
 * @domainObject value-object
 * @invariant `personId` is required. A value naming nobody is not a weaker identity, it is not one.
 * @invariant `userId` is optional and null when there is no account in play.
 * @invariant Frozen, and equal by value. It is replaced rather than edited.
 * @invariant Carries no credential, role or clinic. Those are correctable, or they change without
 *   the person changing, and a stale copy of one is how a decision goes quietly wrong.
 * @typedef {Object} PersonIdentity
 * @property {string} personId - the person record this names
 * @property {string|null} userId - the account in play, or null when there was none
 * @property {string|null} userName - what they were called when this was resolved
 */

/**
 * The only constructor, and not published from `index.js` — the boundary that keeps a caller from
 * assembling a value out of parts it happens to be holding. Nothing here can enforce that: ESLint's
 * import rules do not know what a bounded context is, so `index.test.js` holds it instead.
 *
 * @param {{personId: string, userId?: string|null, userName?: string|null}} parts
 * @returns {PersonIdentity}
 */
export const buildPersonIdentity = ({ personId, userId = null, userName = null }) => {
  if (typeof personId !== 'string' || personId.length === 0) {
    throw new TypeError('PersonIdentity requires a personId');
  }

  return Object.freeze({
    personId,
    userId: typeof userId === 'string' && userId.length > 0 ? userId : null,
    userName: typeof userName === 'string' && userName.length > 0 ? userName : null,
  });
};

/**
 * Whether two values name the same human under the same account.
 *
 * The name is compared too, because it is part of what was recorded rather than a lookup: two
 * records naming one person under names captured years apart hold different values, and saying
 * otherwise would make a rename invisible to anything comparing them.
 *
 * TWO ABSENT IDENTITIES ARE THE SAME ABSENCE, however they are spelled. A record that never named
 * anyone holds null, a field nobody wrote is undefined, and a caller comparing the two is asking
 * whether either names somebody. Answering "different" would report a change between two records
 * that both name nobody.
 *
 * @param {PersonIdentity|null|undefined} a
 * @param {PersonIdentity|null|undefined} b
 * @returns {boolean}
 */
export const isSamePersonIdentity = (a, b) => {
  const aIsAbsent = a === null || a === undefined;
  const bIsAbsent = b === null || b === undefined;

  if (aIsAbsent || bIsAbsent) return aIsAbsent && bIsAbsent;

  return a.personId === b.personId
    && a.userId === b.userId
    && a.userName === b.userName;
};
